import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'ar';
export type LanguageDirection = 'ltr' | 'rtl';

interface LanguageContextValue {
  language: Language;
  direction: LanguageDirection;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: string, fallback?: string) => string;
  formatBrandName: (englishName: string) => string;
}

// Translation keys type for better type safety
type TranslationKey = string;

// Basic translations for common UI elements
const translations: Record<Language, Record<TranslationKey, string>> = {
  en: {
    // Common UI
    search: 'Search',
    select: 'Select',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    
    // Car brands specific
    select_car_brand: 'Select car brand',
    search_brands: 'Search brands...',
    no_brands_found: 'No brands found',
    car_brands: 'Car Brands',
    category: 'Category',
    availability: 'Availability',
    popular_brands: 'Popular Brands',
    luxury_brands: 'Luxury',
    premium_brands: 'Premium', 
    mainstream_brands: 'Mainstream',
    local_popular_brands: 'Local Popular',
    
    // Search and filters
    advanced_filters: 'Advanced Filters',
    clear_filters: 'Clear Filters',
    location: 'Location',
    price_range: 'Price Range',
    brand_features: 'Brand Features',
    official_dealers: 'Has Official Dealers',
    major_network: 'Major Dealership Network',
    electric_vehicles: 'Electric Vehicles',
    
    // Results
    search_results: 'Search Results',
    found_results: 'Found {count} results',
    no_results: 'No results found',
    try_different_search: 'Try adjusting your search terms or filters'
  },
  ar: {
    // Common UI
    search: 'بحث',
    select: 'اختر',
    cancel: 'إلغاء',
    save: 'حفظ',
    delete: 'حذف',
    edit: 'تعديل',
    loading: 'جاري التحميل...',
    error: 'خطأ',
    success: 'نجح',
    
    // Car brands specific
    select_car_brand: 'اختر ماركة السيارة',
    search_brands: 'البحث عن الماركات...',
    no_brands_found: 'لا توجد ماركات',
    car_brands: 'ماركات السيارات',
    category: 'الفئة',
    availability: 'التوفر',
    popular_brands: 'الماركات الشائعة',
    luxury_brands: 'فاخرة',
    premium_brands: 'متميزة',
    mainstream_brands: 'عادية',
    local_popular_brands: 'محلية شائعة',
    
    // Search and filters
    advanced_filters: 'مرشحات متقدمة',
    clear_filters: 'مسح المرشحات',
    location: 'الموقع',
    price_range: 'نطاق السعر',
    brand_features: 'مميزات الماركة',
    official_dealers: 'وكلاء معتمدون',
    major_network: 'شبكة وكالات كبيرة',
    electric_vehicles: 'السيارات الكهربائية',
    
    // Results
    search_results: 'نتائج البحث',
    found_results: 'تم العثور على {count} نتيجة',
    no_results: 'لا توجد نتائج',
    try_different_search: 'حاول تعديل كلمات البحث أو المرشحات'
  }
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  defaultLanguage = 'en' 
}) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check localStorage first, then fall back to default
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('app_language') as Language | null;
      if (saved && ['en', 'ar'].includes(saved)) {
        return saved;
      }
    }
    return defaultLanguage;
  });

  const direction: LanguageDirection = language === 'ar' ? 'rtl' : 'ltr';

  // Update document direction and language when language changes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dir = direction;
      document.documentElement.lang = language;
      
      // Also save to localStorage
      localStorage.setItem('app_language', language);
    }
  }, [language, direction]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const toggleLanguage = () => {
    setLanguageState(prev => prev === 'en' ? 'ar' : 'en');
  };

  // Translation function with improved type safety
  const t = (key: string, fallback?: string): string => {
    try {
      const currentLangTranslations = translations[language];
      const translation = currentLangTranslations[key];
      if (translation) {
        return translation;
      }
      
      // Try fallback language
      const fallbackLang: Language = language === 'en' ? 'ar' : 'en';
      const fallbackLangTranslations = translations[fallbackLang];
      const fallbackTranslation = fallbackLangTranslations[key];
      if (fallbackTranslation) {
        return fallbackTranslation;
      }
    } catch (error) {
      console.warn(`Translation error for key '${key}':`, error);
    }
    
    // Return provided fallback or the key itself
    return fallback || key;
  };

  // Format brand name based on current language
  const formatBrandName = (englishName: string): string => {
    if (language === 'en') {
      return englishName;
    }
    
    // For Arabic, try to get Arabic name
    // This would ideally use the CarBrandArabicUtils, but to avoid circular imports
    // we'll keep it simple here and let components handle the formatting
    return englishName;
  };

  const value: LanguageContextValue = {
    language,
    direction,
    setLanguage,
    toggleLanguage,
    t,
    formatBrandName
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Custom hook for RTL/LTR styles
export const useDirection = () => {
  const { direction, language } = useLanguage();
  
  const isRTL = direction === 'rtl';
  const isArabic = language === 'ar';
  
  // Common style helpers
  const directionStyles = {
    textAlign: isRTL ? 'text-right' : 'text-left',
    marginLeft: isRTL ? 'mr' : 'ml',
    marginRight: isRTL ? 'ml' : 'mr',
    paddingLeft: isRTL ? 'pr' : 'pl', 
    paddingRight: isRTL ? 'pl' : 'pr',
    borderLeft: isRTL ? 'border-r' : 'border-l',
    borderRight: isRTL ? 'border-l' : 'border-r',
    roundedLeft: isRTL ? 'rounded-r' : 'rounded-l',
    roundedRight: isRTL ? 'rounded-l' : 'rounded-r',
    floatLeft: isRTL ? 'float-right' : 'float-left',
    floatRight: isRTL ? 'float-left' : 'float-right'
  } as const;

  return {
    direction,
    isRTL,
    isArabic,
    directionStyles,
    // Helper function to get directional class names
    getDirectionalClass: (ltrClass: string, rtlClass: string): string => 
      isRTL ? rtlClass : ltrClass
  };
};

// Hook for brand name formatting with language support
export const useBrandNameFormatter = () => {
  
  return {
    formatBrandName: (
      englishName: string, 
      mode: 'name_only' | 'with_category' | 'bilingual' = 'name_only'
    ): string => {
      // This would integrate with CarBrandArabicUtils in actual implementation
      // For now, keeping it simple
      return englishName;
    }
  };
};

export default LanguageContext;